import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../components/ui/dialog';
import { Button } from '../../components/ui/button';
import { Package, Layers, ArrowRight } from 'lucide-react';

export default function CartonPackDialog({ open, onClose, product, onAddToCart }) {
  const [selectedMode, setSelectedMode] = useState(null);
  const [packQuantity, setPackQuantity] = useState(1);

  const cartonSize = product?.carton_size || 10;
  const packPrice = product?.sell_price || product?.selling_price || 0;
  const cartonPrice = packPrice * cartonSize * 0.95; // 5% carton discount
  const totalPacksPrice = packPrice * packQuantity;

  const handleSellAsCarton = () => {
    onAddToCart({
      ...product,
      name: `${product.name} (Carton)`,
      sell_price: cartonPrice,
      unit: 'carton',
      _carton_mode: true,
      _packs_in_carton: cartonSize,
    });
    onClose();
  };

  const handleSellAsPacks = () => {
    for (let i = 0; i < packQuantity; i++) {
      onAddToCart({
        ...product,
        name: `${product.name} (Pack)`,
        sell_price: packPrice,
        unit: 'pack',
        _carton_mode: false,
      });
    }
    onClose();
  };

  const handleSellSingle = () => {
    onAddToCart(product);
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-md" data-testid="carton-pack-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Package className="h-5 w-5 text-amber-500" />
            Sell: {product?.name}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-3">
          {/* Option 1: Sell as Carton */}
          <div
            role="button"
            tabIndex={0}
            onClick={() => setSelectedMode('carton')}
            className={`w-full p-4 rounded-lg border text-left transition-colors cursor-pointer ${
              selectedMode === 'carton'
                ? 'bg-amber-500/10 border-amber-500/40'
                : 'bg-slate-100 dark:bg-slate-800/50 border-slate-200 dark:border-slate-700 hover:border-slate-400 dark:hover:border-slate-500'
            }`}
            data-testid="sell-carton-option"
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Layers className="w-8 h-8 text-amber-500" />
                <div>
                  <p className="font-semibold text-slate-800 dark:text-slate-200">Sell as Carton</p>
                  <p className="text-xs text-slate-500">{cartonSize} packs per carton (5% discount)</p>
                </div>
              </div>
              <p className="font-bold text-lg text-amber-600 dark:text-amber-400">${cartonPrice.toFixed(2)}</p>
            </div>
          </div>

          {/* Option 2: Sell as Individual Packs */}
          <div
            role="button"
            tabIndex={0}
            onClick={() => setSelectedMode('packs')}
            className={`w-full p-4 rounded-lg border text-left transition-colors cursor-pointer ${
              selectedMode === 'packs'
                ? 'bg-blue-500/10 border-blue-500/40'
                : 'bg-slate-100 dark:bg-slate-800/50 border-slate-200 dark:border-slate-700 hover:border-slate-400 dark:hover:border-slate-500'
            }`}
            data-testid="sell-packs-option"
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Package className="w-8 h-8 text-blue-500" />
                <div>
                  <p className="font-semibold text-slate-800 dark:text-slate-200">Sell as Pack(s)</p>
                  <p className="text-xs text-slate-500">${packPrice.toFixed(2)} per pack</p>
                </div>
              </div>
              {selectedMode === 'packs' && (
                <div className="flex items-center gap-2">
                  <button
                    onClick={(e) => { e.stopPropagation(); setPackQuantity(Math.max(1, packQuantity - 1)); }}
                    className="w-7 h-7 rounded bg-slate-200 dark:bg-slate-700 flex items-center justify-center text-sm font-bold"
                    data-testid="pack-qty-minus"
                  >-</button>
                  <span className="w-8 text-center font-bold text-slate-800 dark:text-slate-200" data-testid="pack-qty">{packQuantity}</span>
                  <button
                    onClick={(e) => { e.stopPropagation(); setPackQuantity(Math.min(cartonSize, packQuantity + 1)); }}
                    className="w-7 h-7 rounded bg-slate-200 dark:bg-slate-700 flex items-center justify-center text-sm font-bold"
                    data-testid="pack-qty-plus"
                  >+</button>
                </div>
              )}
              {selectedMode !== 'packs' && (
                <p className="font-bold text-lg text-blue-600 dark:text-blue-400">${packPrice.toFixed(2)}</p>
              )}
            </div>
            {selectedMode === 'packs' && (
              <div className="mt-2 text-right text-sm text-blue-600 dark:text-blue-400 font-semibold">
                Total: ${totalPacksPrice.toFixed(2)} ({packQuantity} pack{packQuantity > 1 ? 's' : ''})
              </div>
            )}
          </div>

          {/* Option 3: Add as-is (regular) */}
          <div
            role="button"
            tabIndex={0}
            onClick={() => setSelectedMode('single')}
            className={`w-full p-3 rounded-lg border text-left transition-colors cursor-pointer ${
              selectedMode === 'single'
                ? 'bg-slate-500/10 border-slate-500/40'
                : 'bg-slate-100 dark:bg-slate-800/50 border-slate-200 dark:border-slate-700 hover:border-slate-400 dark:hover:border-slate-500'
            }`}
            data-testid="sell-single-option"
          >
            <div className="flex items-center justify-between">
              <p className="text-sm text-slate-600 dark:text-slate-400">Add at regular price</p>
              <p className="font-semibold text-slate-700 dark:text-slate-300">${packPrice.toFixed(2)}</p>
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="cancel-carton-btn">Cancel</Button>
          <Button
            onClick={() => {
              if (selectedMode === 'carton') handleSellAsCarton();
              else if (selectedMode === 'packs') handleSellAsPacks();
              else if (selectedMode === 'single') handleSellSingle();
            }}
            disabled={!selectedMode}
            className="bg-amber-600 hover:bg-amber-700"
            data-testid="confirm-sell-btn"
          >
            <ArrowRight className="h-4 w-4 mr-2" /> Add to Cart
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
